import { Combobox, ComboboxInput } from "@headlessui/react";
import { AnimatedModal } from "../../AnimatedModal";
import { MagnifyingGlassIcon } from "@heroicons/react/24/solid";
import { useHistory, useParams } from "react-router-dom";
import { useState } from "react";
import { SearchResults } from "./search_results";
import { FolderIcon, MessageCircleQuestionIcon, UserIcon } from "lucide-react";
import {
  ChunkSnippet,
  FileType,
  SearchDealEntityType,
  SearchDealQuery,
  SearchDealResult,
  SearchDealResultType,
  SearchDealType,
} from "@/src/graphql/generated";
import { links } from "@/src/utils/links";
import useGqlClient from "@/src/hooks/useGqlClient";
import { rectToExcelRange } from "@/src/utils/rectToExcelRange";
import { useSelector } from "react-redux";
import { dealSearchSelectors } from "@/src/store/deal-search/selector";
import { getQuestionUrlWithRef } from "@/src/routes/questions/Question";

interface SearchProps {
  open: boolean;
  onClose: () => void;
}

const filterIcons: Record<SearchDealType, React.ReactNode> = {
  [SearchDealType.DataRoom]: <FolderIcon className="size-4 fill-white/60" />,
  [SearchDealType.Questions]: (
    <MessageCircleQuestionIcon className="size-4 fill-white/60" />
  ),
  [SearchDealType.Access]: <UserIcon className="size-4 fill-white/60" />,
};

const filterTypeOptions = [
  { label: "Data Room", value: SearchDealType.DataRoom },
  { label: "Questions", value: SearchDealType.Questions },
  { label: "Access", value: SearchDealType.Access },
];

function getLink(
  result: SearchDealQuery["searchDeal"][0],
  dealId: string,
  snippets: {
    [key: string]: Partial<ChunkSnippet>;
  },
  searchTerm: string,
) {
  if (result.resultType === SearchDealResultType.Chunk) {
    if (!result.chunk) {
      return "/";
    }

    if (result.chunk.excel) {
      if (!result.chunk.dataRoomFile.parentFolder) {
        return "/";
      }

      const link = links.fileLink(
        result.chunk.dataRoomFile.id,
        result.chunk.dataRoomFile.parentFolder.id,
        dealId,
        {
          sheetIndex: result.chunk.excel.sheetIndex,
          cell: rectToExcelRange({
            row0: result.chunk.excel.row0,
            col0: result.chunk.excel.col0,
            row1: result.chunk.excel.row1,
            col1: result.chunk.excel.col1,
          }),
        },
      );

      return link;
    }

    if (
      [FileType.Docx, FileType.Pdf, FileType.Pptx].includes(
        result.chunk.fileType,
      )
    ) {
      const snippet = snippets[`${searchTerm}-${result.chunk.id}`];
      if (snippet) {
        if (!result.chunk.dataRoomFile.parentFolder) {
          return "/";
        }

        const link = links.fileLink(
          result.chunk.dataRoomFile.id,
          result.chunk.dataRoomFile.parentFolder.id,
          dealId,
          {
            pageIndex: snippet.location?.pageIndex,
            rectsOnPage: snippet.location?.rectsOnPage,
          },
        );

        return link;
      }
    }
  }

  if (result.resultType === SearchDealResultType.Entity) {
    if (!result.entity) {
      return "/";
    }

    const res = result.entity;

    switch (res.entityType) {
      case SearchDealEntityType.DataRoomFile:
        if (!res.dataRoomFile || !res.dataRoomFile.parentFolder) {
          // If file has no parent folder, but has a question, link to question page with file slide-over open
          if (res.dataRoomFile?.questionId) {
            return getQuestionUrlWithRef(
              dealId,
              res.dataRoomFile.questionId,
              res.dataRoomFile.id,
            );
          }

          return "/";
        }

        return links.fileLink(
          res.dataRoomFile.id,
          res.dataRoomFile.parentFolder.id,
          dealId,
        );
      case SearchDealEntityType.DataRoomFolder:
        if (!res.dataRoomFolder) {
          return "/";
        }
        return links.folderLink(res.dataRoomFolder.id, dealId);
      case SearchDealEntityType.Question:
        if (!res.question) {
          return "/";
        }

        return links.questionLink(res.question, dealId);
      case SearchDealEntityType.QuestionAnswer:
        if (!res.questionAnswer) {
          return "/";
        }
        return links.questionLink(res.questionAnswer.question, dealId);
      case SearchDealEntityType.QuestionAnswerFile:
        if (!res.questionAnswer) {
          return "/";
        }
        return links.questionLink(res.questionAnswer.question, dealId);
      case SearchDealEntityType.Ddql:
        if (!res.ddql) {
          return "/";
        }
        return links.ddqlLink(res.ddql, dealId);
      case SearchDealEntityType.DealAccount:
        if (!res.dealAccount) {
          return "/";
        }
        return links.dealAccountLink(res.dealAccount.id, dealId);
      default:
        return null;
    }
  }
}

export function Search(props: SearchProps) {
  const { open, onClose } = props;
  const history = useHistory();
  const { dealId } = useParams<{ dealId: string }>();
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState<SearchDealType | null>(null);

  const snippets = useSelector(dealSearchSelectors.snippets);

  const client = useGqlClient();

  return (
    <AnimatedModal
      open={open}
      onClose={() => {
        setSearchTerm("");
        setFilterType(null);

        onClose();
      }}
      position="top"
      size="xxxxl"
      padding="p-0"
    >
      <Combobox
        onChange={(v: SearchDealResult) => {
          const link = getLink(v, dealId, snippets, searchTerm);
          if (link) {
            history.push(link);
            setSearchTerm("");
            setFilterType(null);
            onClose();
          }
        }}
      >
        <div className="flex flex-col h-full ">
          <div className="relative">
            <MagnifyingGlassIcon
              className="pointer-events-none absolute left-4 top-3.5 h-5 w-5 text-gray-400"
              aria-hidden="true"
            />
            <ComboboxInput
              autoFocus
              className="h-12 w-full border-b select-none border-gray-100 rounded-t-md focus:outline-none focus:select-none focus:border-0 focus:border-b focus:border-gray-100 bg-transparent pl-11 pr-4 text-gray-900 placeholder:text-gray-400 focus:ring-0 sm:text-sm"
              placeholder="Search for..."
              onChange={(event) => {
                setSearchTerm(event.currentTarget.value);
              }}
              value={searchTerm}
            />
          </div>
          <div className="flex-1">
            <SearchResults
              searchTerm={searchTerm}
              recentSearches={[]}
              onClose={onClose}
              searchDealType={filterType ?? undefined}
            />
          </div>
        </div>
      </Combobox>
    </AnimatedModal>
  );
}
